import { useQuery } from 'react-query'
import { request } from 'Features/auth/axios-client'
import {
  FetchSourceSiteDetailsParams,
  SourceSiteCapacityTableParams,
} from './SourceSiteDashboard.types'

type GraphAPIParams = {
  workcentresList: string[]
  planId: string
}

const fetchVolumeData = async ({ planId, workcentresList }: GraphAPIParams) => {
  const query = workcentresList?.join(',')
  const { data } = await request({
    url: `plan/${planId}/source-sites/${10}/volume-graph?workcentres=${query}`,
  })
  return data
}

export const useSourceSiteVolumeData = (params: GraphAPIParams) => {
  return useQuery(
    [
      'volume-chart',
      params.planId,
      params?.workcentresList ? params.workcentresList.join(',') : '',
    ],
    () => fetchVolumeData(params),
    {
      refetchOnWindowFocus: false,
      select: (data) => data?.data,
      cacheTime: 0,
      enabled: params.workcentresList?.length > 0,
    }
  )
}

const fetchCapacity = async ({ planId, workcentresList }: GraphAPIParams) => {
  const query = workcentresList?.join(',')
  const data = await request({
    url: `plan/${planId}/source-sites/${10}/capacity-graph?workcentres=${query}`,
  })
  return data
}

export const useCapacity = (params: GraphAPIParams) => {
  return useQuery(
    ['capacity', params.planId, params?.workcentresList?.join(',')],
    () => fetchCapacity(params),
    {
      refetchOnMount: false,
      select: (data) => data?.data?.data,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: params.workcentresList?.length > 0,
    }
  )
}

const fetchStockProjection = async ({
  planId,
  workcentresList,
}: GraphAPIParams) => {
  const query = workcentresList?.join(',')
  const data = await request({
    url: `plan/${planId}/source-sites/${10}/stock-projection-graph?workcentres=${query}`,
  })
  return data
}

export const useStockProjection = (params: GraphAPIParams) => {
  return useQuery(
    [
      'stock-projection-dashboard-chart',
      params.planId,
      params?.workcentresList?.join(','),
    ],
    () => fetchStockProjection(params),
    {
      refetchOnMount: false,
      select: (data) => data?.data?.data,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: params.workcentresList?.length > 0,
    }
  )
}

const fetchExcessData = async (planId: string, workcentresList: string[]) => {
  const query = workcentresList?.join(',')
  const { data } = await request({
    url: `plan/${planId}/source-sites/${10}/excess-graph?workcentres=${query}`,
  })
  return data
}

export const useExcessData = (planId: string, workcentresList: string[]) => {
  return useQuery(
    ['stock-build-excess', planId, workcentresList?.join(',')],
    () => fetchExcessData(planId, workcentresList),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      select: (data) => data ?? [],
      enabled: workcentresList?.length > 0,
    }
  )
}

const fetchCapacityTable = async ({
  sourcesiteId,
  planId,
  selectedDate,
}: SourceSiteCapacityTableParams) => {
  const data = await request({
    url: `plan/${planId}/source-sites/${sourcesiteId}/capacity-table?date=${selectedDate}`,
  })
  return data
}

export const useCapacityTable = (params: SourceSiteCapacityTableParams) => {
  return useQuery(
    ['capacity-table', params.planId, params.selectedDate, params.sourcesiteId],
    () => fetchCapacityTable(params),
    {
      refetchOnMount: false,
      select: (data) => data?.data?.data,
      enabled: params.selectedDate !== null,
      cacheTime: 0,
    }
  )
}
